import { useQuery } from "@tanstack/react-query";
import { db } from "@/modules/shared/lib/db-client";
import { log } from "@/modules/shared/lib/observability";

export interface HowItWorksItem {
  step: string;
  title: string;
  description: string;
  icon: string;
}

export interface PrizeItem {
  place: string;
  prize: string;
  icon: string;
}

export interface SponsorLogo {
  name: string;
  logo_url: string;
  url?: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface ChampionshipTenantConfig {
  organization_id: string;
  championship_name: string;
  championship_slug: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  hero_title: string | null;
  hero_subtitle: string | null;
  registration_open: boolean;
  max_participants: number;
  registration_offset: number;
  rules_url: string | null;
  rules_content: string | null;
  contact_email: string | null;
  email_from_name: string | null;
  email_from_address: string | null;
  meta_title: string | null;
  meta_description: string | null;
  og_image_url: string | null;
  custom_css: string | null;
  partnership_text: string | null;
  restrict_email_domain: boolean;
  allowed_email_domains: string[];
  // New LP config fields
  hero_bg_image_url: string | null;
  hero_badge_image_url: string | null;
  cta_text: string | null;
  cta_secondary_text: string | null;
  prizes_config: PrizeItem[] | null;
  how_it_works_config: HowItWorksItem[] | null;
  sponsor_logos: SponsorLogo[] | null;
  faq_config: FAQItem[] | null;
}

// Default config for the main OpenCode instance
const DEFAULT_CONFIG: ChampionshipTenantConfig = {
  organization_id: "00000000-0000-0000-0000-000000000001",
  championship_name: "OpenCode Championship",
  championship_slug: "opencode",
  logo_url: null,
  logo_dark_url: null,
  favicon_url: null,
  primary_color: "#059669",
  secondary_color: "#0f172a",
  accent_color: "#CED23C",
  hero_title: "O Maior Torneio de Programação 1v1",
  hero_subtitle: null,
  registration_open: true,
  max_participants: 512,
  registration_offset: 334,
  rules_url: null,
  rules_content: null,
  contact_email: null,
  email_from_name: "OpenHub",
  email_from_address: "no-reply@openhubr.com",
  meta_title: "Openhub | Opencode",
  meta_description: "Join Opencode — the 1v1 programming championship by OpenCircle.",
  og_image_url: null,
  custom_css: null,
  partnership_text: null,
  restrict_email_domain: false,
  allowed_email_domains: [],
  hero_bg_image_url: null,
  hero_badge_image_url: null,
  cta_text: "Inscreva-se Agora — É Gratuito",
  cta_secondary_text: "Continuar Inscrição",
  prizes_config: [
    { place: "1º Lugar", prize: "A definir", icon: "trophy" },
    { place: "2º Lugar", prize: "A definir", icon: "medal" },
    { place: "3º Lugar", prize: "A definir", icon: "award" },
  ],
  how_it_works_config: [
    { step: "01", title: "Inscrição Aberta", description: "Qualquer dev pode participar — do iniciante ao sênior.", icon: "users" },
    { step: "02", title: "Qualificação Online", description: "Resolva desafios de código. Os melhores classificados avançam.", icon: "code" },
    { step: "03", title: "Chaves Eliminatórias", description: "Duelos 1v1 em tempo real. Quem resolver primeiro vence.", icon: "swords" },
    { step: "04", title: "Grande Final", description: "Finalistas se enfrentam ao vivo em um palco de e-sports.", icon: "crown" },
  ],
  sponsor_logos: [
    { name: "Amazon Web Services", logo_url: "https://d0.awsstatic.com/logos/powered-by-aws-white.png", url: "https://aws.amazon.com" },
  ],
  faq_config: null,
};

type RawTenantConfig = Omit<
  Partial<ChampionshipTenantConfig>,
  "allowed_email_domains" | "prizes_config" | "how_it_works_config" | "sponsor_logos" | "faq_config"
> & {
  allowed_email_domains?: string[] | null;
  prizes_config?: PrizeItem[] | null;
  how_it_works_config?: HowItWorksItem[] | null;
  sponsor_logos?: SponsorLogo[] | null;
  faq_config?: FAQItem[] | null;
};

function normalizeTenantConfig(rawConfig?: RawTenantConfig | null): ChampionshipTenantConfig {
  return {
    ...DEFAULT_CONFIG,
    ...rawConfig,
    championship_name: rawConfig?.championship_name || DEFAULT_CONFIG.championship_name,
    championship_slug: rawConfig?.championship_slug || DEFAULT_CONFIG.championship_slug,
    primary_color: rawConfig?.primary_color || DEFAULT_CONFIG.primary_color,
    secondary_color: rawConfig?.secondary_color || DEFAULT_CONFIG.secondary_color,
    accent_color: rawConfig?.accent_color || DEFAULT_CONFIG.accent_color,
    hero_title: rawConfig?.hero_title || DEFAULT_CONFIG.hero_title,
    hero_subtitle: rawConfig?.hero_subtitle ?? DEFAULT_CONFIG.hero_subtitle,
    registration_open: rawConfig?.registration_open ?? DEFAULT_CONFIG.registration_open,
    max_participants: rawConfig?.max_participants ?? DEFAULT_CONFIG.max_participants,
    registration_offset: rawConfig?.registration_offset ?? DEFAULT_CONFIG.registration_offset,
    email_from_name: rawConfig?.email_from_name || DEFAULT_CONFIG.email_from_name,
    email_from_address: rawConfig?.email_from_address || DEFAULT_CONFIG.email_from_address,
    meta_title: rawConfig?.meta_title || DEFAULT_CONFIG.meta_title,
    meta_description: rawConfig?.meta_description || DEFAULT_CONFIG.meta_description,
    restrict_email_domain: rawConfig?.restrict_email_domain ?? DEFAULT_CONFIG.restrict_email_domain,
    allowed_email_domains: rawConfig?.allowed_email_domains || DEFAULT_CONFIG.allowed_email_domains,
    prizes_config: rawConfig?.prizes_config || DEFAULT_CONFIG.prizes_config,
    how_it_works_config: rawConfig?.how_it_works_config || DEFAULT_CONFIG.how_it_works_config,
    sponsor_logos: (rawConfig?.sponsor_logos && rawConfig.sponsor_logos.length > 0) ? rawConfig.sponsor_logos : DEFAULT_CONFIG.sponsor_logos,
    faq_config: rawConfig?.faq_config || DEFAULT_CONFIG.faq_config,
  };
}

/**
 * Resolves tenant from a route slug parameter.
 */
export function useChampionshipTenant(slug?: string | null) {
  const isMainInstance = !slug;

  const { data: tenantConfig, isLoading } = useQuery({
    queryKey: ["championship-tenant", slug || "__main__"],
    queryFn: async () => {
      if (!slug) {
        const { data, error } = await db
          .from("championship_config")
          .select("*")
          .eq("championship_slug", "opencode")
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error || !data) {
          log.warn("Failed to resolve main championship config");
          return DEFAULT_CONFIG;
        }

        return normalizeTenantConfig(data as unknown as RawTenantConfig);
      }

      const { data, error } = await db.rpc(
        "resolve_championship_subdomain",
        { p_subdomain: slug }
      );

      if (error || !data) {
        log.warn("Failed to resolve championship tenant", { slug: slug || "" });
        return DEFAULT_CONFIG;
      }

      return normalizeTenantConfig(data as unknown as RawTenantConfig);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const basePath = slug ? `/opencode/${slug}` : "/opencode";

  return {
    config: isLoading && !isMainInstance ? null : (tenantConfig || DEFAULT_CONFIG),
    isLoading,
    isMainInstance,
    slug: slug || null,
    basePath,
  };
}
